import { isTerminalAuthError, signalSessionExpired } from '@/lib/auth/session';
import { getUserManager } from '@/lib/auth/userManager';

export const SESSION_EXPIRED_MESSAGE = 'Session expired. Signing back in…';
/** Life a token handed to a paused turn must have left: well past know-agent's 60 s margin, so the worker never refuses it at once. */
export const FRESH_TOKEN_SECONDS = 180;
export const REFRESH_FAILED_MESSAGE = 'Could not refresh the session. Retry in a moment.';

function expired(cause?: unknown): Error {
  signalSessionExpired();
  return new Error(SESSION_EXPIRED_MESSAGE, { cause });
}

export interface AccessTokenOptions {
  /**
   * Renew a token with this many seconds or fewer left, rather than hand out
   * one a service will refuse moments later. Renewal is best effort while
   * the token is still valid: a transient failure returns the current one.
   */
  minValiditySeconds?: number;
  /** Renew even a token with time left, because a service refused it as stale. */
  forceRefresh?: boolean;
}

/**
 * The current access token, refreshed through the single-flight manager when
 * it has expired. Calls the UserManager directly: react-oidc-context's
 * `useAuth().signinSilent` swallows errors (returning null) and toggles
 * `isLoading`, which hid terminal failures from SessionRecovery.
 *
 * `minValiditySeconds` refreshes a live token with less life left than that.
 * A dead grant signals SessionRecovery and throws; a transient failure throws
 * a retryable error. Neither falls back to an anonymous request.
 */
export async function getAccessToken({ minValiditySeconds = 0, forceRefresh = false }: AccessTokenOptions = {}): Promise<string | undefined> {
  const manager = getUserManager();
  const user = await manager.getUser();
  if (!user) return undefined;
  const early = !user.expired && (forceRefresh || (user.expires_in ?? Infinity) <= minValiditySeconds);
  if (!user.expired && !early) return user.access_token;
  if (!user.refresh_token) {
    if (early) return user.access_token;
    throw expired();
  }
  let renewed;
  try {
    renewed = await manager.signinSilent();
  } catch (err) {
    if (isTerminalAuthError(err)) throw expired(err);
    if (early) return user.access_token;
    throw new Error(REFRESH_FAILED_MESSAGE, { cause: err });
  }
  if (!renewed) throw expired();
  return renewed.access_token;
}
